import { useMemo } from 'react'
import { useCaseContext } from '@broker-crm-contexts'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Link } from '@phosphor-icons/react'
import isEmpty from 'lodash/isEmpty'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { useFormatMessage } from '@acre/utils'
import { RelatedCase } from '@acre/graphql'
import { ButtonNewest, STATUS_INTL, STATUS_TAG_COLOUR, Tag } from '@acre/design-system'

import { mortgageReasonMap } from '../../../pages/Cases/CaseDetails/CaseOverview/CaseSummary/CaseSummary.helpers'
import CollapsableSection, { SidebarSection } from '../CollapsableSection'
import { CardTableCell } from '../Info/CaseInfo'
import { LINKED_CASE_TYPE_MAP, messagePrefix } from './RelatedCases.helpers'
import { InfoTable } from '../Info/CaseInfo.styles'
import { SectionWrapper } from '../ReviewModeDrawer.styles'

const RelatedCases = () => {
  const formatMessage = useFormatMessage()
  const theme = useTheme()
  const navigate = useNavigate()
  const caseContext = useCaseContext()

  const relatedCases = useMemo(() => caseContext?.details?.related_cases ?? [], [caseContext?.details?.related_cases])

  if (isEmpty(relatedCases)) {
    return (
      <SectionWrapper>
        <CollapsableSection
          sectionId={SidebarSection.Tasks}
          header={formatMessage(`${messagePrefix}.title`, { count: relatedCases?.length })}
        >
          <Typography fontSize={theme.typography.pxToRem(14)}>
            {formatMessage(`${messagePrefix}.noRelatedCases`)}
          </Typography>
        </CollapsableSection>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper>
      <CollapsableSection
        sectionId={SidebarSection.Tasks}
        header={formatMessage(`${messagePrefix}.title`, { count: relatedCases?.length })}
      >
        <Stack spacing={2}>
          {relatedCases.map((relatedCase: RelatedCase) => {
            const { id, relationship_type, case_id, visible, firm, advisor, status, preference_mortgage_reason } =
              relatedCase

            if (!relationship_type || !status || !preference_mortgage_reason) return

            return (
              <Box
                key={`RelatedCase${id}`}
                boxShadow={1}
                border={1}
                borderColor={theme.colours.baseLight}
                borderRadius={1}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={2}
                  px={2}
                  borderColor={theme.colours.transparent}
                >
                  {visible ? (
                    <ButtonNewest
                      variant="link"
                      onClick={() => navigate(`/cases/${case_id}/overview`)}
                      sx={{
                        fontSize: theme.typography.pxToRem(12),
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1.6,
                        textAlign: 'left',
                      }}
                    >
                      <Box mr={0.5} display="flex" alignItems="center">
                        <Link size={16} />
                      </Box>
                      <Box component="strong" whiteSpace="normal">
                        {formatMessage(LINKED_CASE_TYPE_MAP[relationship_type])}
                      </Box>
                    </ButtonNewest>
                  ) : (
                    <Typography fontWeight="bold" fontSize={theme.typography.pxToRem(12)} lineHeight={1.6}>
                      <FormattedMessage id={mortgageReasonMap[preference_mortgage_reason]} />
                    </Typography>
                  )}
                  <Tag text={formatMessage(STATUS_INTL[status])} colourID={STATUS_TAG_COLOUR[status]} />
                </Box>

                <Box p={2}>
                  <InfoTable>
                    <tbody>
                      <tr>
                        <CardTableCell color="secondary">
                          <p>{formatMessage(`${messagePrefix}.firm`)}</p>
                        </CardTableCell>
                        <CardTableCell textAlign="right">{firm}</CardTableCell>
                      </tr>
                      {advisor && (
                        <tr>
                          <CardTableCell color="secondary">
                            <p>{formatMessage(`${messagePrefix}.advisor`)}</p>
                          </CardTableCell>
                          <CardTableCell textAlign="right">{advisor}</CardTableCell>
                        </tr>
                      )}
                    </tbody>
                  </InfoTable>
                </Box>
              </Box>
            )
          })}
        </Stack>
      </CollapsableSection>
    </SectionWrapper>
  )
}

export default RelatedCases
